import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  ViewChild,
  AfterViewInit,
  ViewEncapsulation,
  OnDestroy,
  HostBinding,
  ChangeDetectorRef
} from '@angular/core';
import { BaseGridSource } from './grid-data-source';
import { GridConfiguration, ColumnDisplayType, FilterType } from './models';
import { UntypedFormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { merge, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap, first } from 'rxjs/operators';
import { GridService } from './grid.service';
import { Router, ActivatedRoute } from '@angular/router';
import { SelectionModel } from '@angular/cdk/collections';

@Component({
  standalone: false,
  selector: 'lib-grid',
  templateUrl: './grid.component.html',
  styleUrls: ['./grid.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [GridService]
})
export class GridComponent implements OnInit, AfterViewInit, OnDestroy {
  @HostBinding('class') gridClass = 'lib-grid';
  @HostBinding('class.mat-elevation-z2') get gridElevation() {
    return this.gridConfiguration?.elevation;
  }

  privDataset: BaseGridSource<any>;

  @Input()
  get dataSource(): BaseGridSource<any> {
    return this.privDataset;
  }

  set dataSource(e) {
    if (this.privDataset !== e) {
      this.initialFilterValuesRestored = false;
    }
    this.privDataset = e;
    if (this.skipLoadOnInit) {
    } else if (!this.loadAfterInit || this.viewInitialized) {
      this.scheduleLoadData(true);
    }
  }

  @Input()
  gridConfiguration: GridConfiguration;

  @Input()
  filterStringParameter: string;

  @Input()
  enableFilter: boolean = true;

  @Input()
  clearFilterOnInitialLoad: boolean = false;

  @Input()
  restoreFilterValuesAfterInitialLoad: boolean = false;

  @Input() skipLoadOnInit: string;

  @Input() loadAfterInit: boolean = false;

  @Input()
  pageSizeOptions = [25, 50, 100, 250, 500];

  @Input()
  sortColumn: string;

  @Input()
  sortOrder: number;

  filterColumns = [];

  filterParams = {};

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  get hasFilters(): boolean {
    return this.gridConfiguration?.columnDefinitions?.some((x) => x.filterable === true) || false;
  }

  columnTypes = ColumnDisplayType;
  selection = new SelectionModel(true, []);
  selectedRow: any;
  private viewInitialized = false;
  private initialLoadTimeout: ReturnType<typeof setTimeout>;
  private initialFilterValuesRestored = false;

  public filterFormGroup: UntypedFormGroup;
  subscriptions: Subscription = new Subscription();
  constructor(
    public gridService: GridService,
    public router: Router,
    public route: ActivatedRoute,
    private cdRef: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.filterFormGroup = new UntypedFormGroup({});
  }

  ngAfterViewInit() {
    this.viewInitialized = true;
    if (this.loadAfterInit) {
      this.scheduleLoadData();
    }

    this.paginator?.page.subscribe((x) => {
      sessionStorage.setItem('pageSize', x.pageSize.toString());
      sessionStorage.setItem('pageIndex', x.pageIndex.toString());
    });

    const formSub = this.filterFormGroup.valueChanges
      .pipe(
        debounceTime(750),
        distinctUntilChanged(),
        tap(() => {
          if (this.enableFilter) {
            this.paginator.firstPage();
            this.loadData();
          }
        })
      )
      .subscribe();

    if (formSub) {
      this.subscriptions.add(formSub);
    }

    if (this.sort?.sortChange && this.paginator?.page) {
      // Wires paging and sorting to loading';
      const sortPageSub = merge(this.sort?.sortChange, this.paginator?.page)
        .pipe(
          tap(() => {
            this.loadData();
          })
        )
        .subscribe();
      this.subscriptions.add(sortPageSub);
    }

    if (this.filterStringParameter && this.route.queryParams) {
      setTimeout(() => {
        this.subscriptions.add(
          this.route.queryParams.pipe(first()).subscribe((params) => {
            if (params[this.filterStringParameter]) {
              this.filterFormGroup.patchValue(JSON.parse(atob(decodeURIComponent(params[this.filterStringParameter]))));
            }
          })
        );
      }, 0);
    }
  }

  ngOnDestroy() {
    if (this.initialLoadTimeout) {
      clearTimeout(this.initialLoadTimeout);
    }
    this.subscriptions.unsubscribe();
  }

  private scheduleLoadData(init: boolean = false) {
    if (this.initialLoadTimeout) {
      clearTimeout(this.initialLoadTimeout);
    }
    this.initialLoadTimeout = setTimeout(() => {
      this.initialLoadTimeout = null;
      this.loadData(init);
      this.cdRef.markForCheck();
    }, 0);
  }
  /**
   * Dispatches load events
   *
   * @memberof GridComponent
   */
  async loadData(init: boolean = false) {
    this.selection.clear();
    this.selectedRow = null;
    if (this.dataSource) {
      if (!this.sort?.active && this.sortColumn) {
        this.sort.active = this.sortColumn;
        this.sort.direction = this.sortOrder === 1 ? 'desc' : 'asc';
      }
      this.dataSource.load(
        this.gridService.buildPagedModel(this.paginator, this.sort, this.gridConfiguration, this.filterFormGroup)
      );
      if (this.filterStringParameter && this.filterFormGroup) {
        const cleanFilterParams = {};
        Object.keys(this.filterFormGroup.value).forEach((k) => {
          if (this.filterFormGroup.value[k] !== '') {
            if (!(init && this.clearFilterOnInitialLoad)) {
              cleanFilterParams[k] = this.filterFormGroup.value[k];
            } else {
              this.filterFormGroup.controls[k].setValue('');
            }
          }
        });
        await this.router.navigate(['./'], {
          relativeTo: this.route,
          queryParams: {
            [this.filterStringParameter]: encodeURIComponent(btoa(JSON.stringify(cleanFilterParams)))
          }
        });
      }
      this.restoreInitialFilterValues();
      localStorage.setItem('filterParams', JSON.stringify(this.filterParams));

    }
  }

  private restoreInitialFilterValues(): void {
    if (!this.restoreFilterValuesAfterInitialLoad || this.initialFilterValuesRestored) {
      return;
    }

    const values = {};
    this.gridConfiguration?.columnDefinitions?.forEach((column) => {
      if (!column.filterable) {
        return;
      }

      switch (column.filterConfig?.filterType) {
        case FilterType.number:
          if (column.filterConfig.initialStart !== undefined) {
            values[`${column.displayKey}-start`] =
              column.filterConfig.initialStart === -999999999 ? '' : column.filterConfig.initialStart;
          }
          if (column.filterConfig.initialEnd !== undefined) {
            values[`${column.displayKey}-end`] =
              column.filterConfig.initialEnd === 999999999 ? '' : column.filterConfig.initialEnd;
          }
          break;
        case FilterType.date:
          if (column.filterConfig.initialStart !== undefined) {
            values[`${column.displayKey}-start`] = column.filterConfig.initialStart
              ? new Date(column.filterConfig.initialStart)
              : '';
          }
          if (column.filterConfig.initialEnd !== undefined) {
            values[`${column.displayKey}-end`] = column.filterConfig.initialEnd
              ? new Date(column.filterConfig.initialEnd)
              : '';
          }
          break;
        default:
          if (column.filterConfig?.initial !== undefined) {
            values[column.displayKey] = column.filterConfig.initial;
          }
          break;
      }
    });

    this.filterFormGroup.patchValue(values, { emitEvent: false });
    this.initialFilterValuesRestored = true;
    this.cdRef.markForCheck();
  }

  /**
   * Event Fired on row click
   *
   * @param {*} rowValue
   * @memberof GridComponent
   */
  rowClick($event, rowValue) {
    if (this.gridConfiguration?.rowClickEvent) {
      this.gridConfiguration?.rowClickEvent(rowValue);
      if (this.gridConfiguration?.rowSelectable) {
        this.selectedRow = rowValue;
      }
    }
  }

  toggleRow($event, rowValue) {
    if (this.gridConfiguration?.rowExpandable) {
    }
  }

  cellClick(event: MouseEvent, columnKey: string, rowValue: any) {
    const columnDef = this.gridConfiguration.columnDefinitions.find((x) => x.displayKey === columnKey);
    if (columnDef?.cellClickEvent) {
      event.stopPropagation();
      this.selectedRow = null;
      columnDef.cellClickEvent(rowValue);
    }
  }

  cellActionButtonClick(event: MouseEvent, columnKey: string, rowValue: any) {
    const columnDef = this.gridConfiguration.columnDefinitions.find((x) => x.displayKey === columnKey);
    if (columnDef?.cellActionButton?.clickEvent) {
      event.stopPropagation();
      this.selectedRow = null;
      columnDef?.cellActionButton?.clickEvent(rowValue);
    }
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = !!this.dataSource && this.dataSource.dataSourceSubject.value.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected()
      ? this.selection.clear()
      : this.dataSource.dataSourceSubject.value.forEach((x) => this.selection.select(x));
  }

  toggleCheckbox(row): void {
    this.selection.toggle(row);
  }

  camelCaseRemoveSpaces(element) {
    return element.replace(/\s/g, '').replace(/^\w/, (c) => c.toLowerCase());
  }

  getMultilineCellLines(value: any): any[] {
    if (Array.isArray(value)) {
      return value;
    }

    return value == null ? [''] : String(value).split('\n');
  }

  trackByIndex(index: number, item: any) {
    return index;
  }
  trackById(index: number, item: any) {
    return item.id;
  }
}
